/**
 * CircleOfOriginCard — Root Pattern Assessment
 * Parallel pillar to Purusharthas inside the Relationship Wisdom Coaching module.
 *
 * Exports:
 *   • PurusharthasAwarenessPair-equivalent: CircleOfOriginAwarenessPair
 *       Renders the standard 2-column paired row (LEFT = 4 trait accordions with
 *       Likert/Slider/MCQ questions + Save, RIGHT = classification + scores +
 *       false/authentic perception + safety banner). Use on the Awareness tab.
 *   • Default export CircleOfOriginCard: full-width card for Alignment / Awakening
 *       / Harmony tabs, showing the user's primary bucket prominently with the
 *       other 7 collapsed accordions for browsing.
 *
 * Item-key convention (compatible with /dashboard/pipeline-progress/toggle):
 *   rel_{tab}_coo_{bucket_or_universal}_{idx}
 *   e.g.  rel_alignment_coo_anxious_maternal_0, rel_harmony_coo_universal_2
 */
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, Loader2, Save, Check, AlertCircle, Sparkles, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { apiCall } from "../App";

const TRAIT_COLORS = {
  rose:    { dot: "bg-rose-400",    text: "text-rose-300",    border: "border-rose-400/30",    bgSoft: "bg-rose-500/5",    bar: "bg-rose-400" },
  sky:     { dot: "bg-sky-400",     text: "text-sky-300",     border: "border-sky-400/30",     bgSoft: "bg-sky-500/5",     bar: "bg-sky-400" },
  amber:   { dot: "bg-amber-400",   text: "text-amber-300",   border: "border-amber-400/30",   bgSoft: "bg-amber-500/5",   bar: "bg-amber-400" },
  violet:  { dot: "bg-violet-400",  text: "text-violet-300",  border: "border-violet-400/30",  bgSoft: "bg-violet-500/5",  bar: "bg-violet-400" },
};

const CLASSIFICATION_LABEL = {
  secure: "Secure",
  anxious: "Anxious (Preoccupied)",
  avoidant: "Avoidant (Dismissive)",
  disorganized: "Disorganized (Fearful-Avoidant)",
};
const CLASSIFICATION_COLOR = {
  secure:       "text-emerald-300 border-emerald-400/40 bg-emerald-500/10",
  anxious:      "text-rose-300 border-rose-400/40 bg-rose-500/10",
  avoidant:     "text-sky-300 border-sky-400/40 bg-sky-500/10",
  disorganized: "text-violet-300 border-violet-400/40 bg-violet-500/10",
};
const SEVERITY_LABEL = { low: "Low", moderate: "Moderate", high: "High" };
const SEVERITY_COLOR = {
  low:      "text-emerald-300 border-emerald-400/40 bg-emerald-500/10",
  moderate: "text-amber-300 border-amber-400/40 bg-amber-500/10",
  high:     "text-rose-300 border-rose-400/40 bg-rose-500/10",
};
const ORIGIN_LABEL = { maternal: "Maternal", paternal: "Paternal", dual: "Dual" };

const BUCKET_LABEL = {
  anxious_maternal:  "Anxious · Maternal Origin",
  anxious_paternal:  "Anxious · Paternal Origin",
  anxious_dual:      "Anxious · Dual Origin",
  avoidant_maternal: "Avoidant · Maternal Origin",
  avoidant_paternal: "Avoidant · Paternal Origin",
  avoidant_dual:     "Avoidant · Dual Origin",
  disorganized_any:  "Disorganized · any origin",
  secure_any:        "Secure · Low Impact",
};
const ALL_BUCKETS = [
  "anxious_maternal", "anxious_paternal", "anxious_dual",
  "avoidant_maternal", "avoidant_paternal", "avoidant_dual",
  "disorganized_any", "secure_any",
];

function itemKeyFor(tab, bucketOrSection, idx) {
  return `rel_${tab}_coo_${bucketOrSection}_${idx}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Question input controls
// ═══════════════════════════════════════════════════════════════════════════

function LikertScale({ q, value, onChange, testid }) {
  // Discrete 1-5 chip scale with low/high labels
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex justify-between text-[10px] text-chakra-crown/50">
        <span>{q.low_label}</span>
        <span>{q.high_label}</span>
      </div>
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map(v => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(value === v ? null : v)}
            data-testid={`${testid}-v${v}`}
            className={`flex-1 py-2 rounded-lg text-xs font-heading border transition-all ${
              value === v
                ? "bg-divine-gold text-cosmic-void border-divine-gold shadow-[0_0_10px] shadow-divine-gold/40"
                : "bg-cosmic-void/40 text-chakra-crown/70 border-divine-gold/15 hover:border-divine-gold/40"
            }`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

function SliderScale({ q, value, onChange, testid }) {
  const v = value ?? 3;
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex justify-between text-[10px] text-chakra-crown/50">
        <span>{q.low_label}</span>
        <span>{q.high_label}</span>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={1}
          max={5}
          step={1}
          value={v}
          onChange={e => onChange(parseInt(e.target.value, 10))}
          data-testid={`${testid}-range`}
          className="flex-1 accent-divine-gold"
        />
        <span className="w-8 text-center text-sm font-heading text-divine-gold tabular-nums">{value ?? "—"}</span>
      </div>
      {value == null && (
        <button type="button" onClick={() => onChange(3)}
          data-testid={`${testid}-init`}
          className="text-[10px] text-chakra-crown/45 italic hover:text-divine-gold">
          Tap to set initial value (3)
        </button>
      )}
    </div>
  );
}

function MCQChoice({ q, value, onChange, testid }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {q.options.map(opt => {
        const selected = String(value) === String(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(selected ? null : opt.value)}
            data-testid={`${testid}-${opt.value}`}
            className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-all flex items-start gap-2.5 ${
              selected
                ? "bg-divine-gold/15 border-divine-gold text-chakra-crown"
                : "bg-cosmic-void/40 border-divine-gold/15 text-chakra-crown/75 hover:border-divine-gold/40"
            }`}
          >
            <span className={`shrink-0 w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center text-[10px] font-heading mt-0.5 ${
              selected ? "border-divine-gold bg-divine-gold text-cosmic-void" : "border-divine-gold/40 text-chakra-crown/60"
            }`}>{opt.value}</span>
            <span className="leading-relaxed">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function QuestionRow({ q, value, onChange }) {
  const testid = `coo-q-${q.id}`;
  return (
    <div className="rounded-xl border border-divine-gold/15 bg-cosmic-void/30 p-4 space-y-3" data-testid={testid}>
      <div className="flex items-start gap-3">
        <span className="shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-heading text-chakra-crown/55 bg-cosmic-void/60 border border-divine-gold/15">{q.id}</span>
        <p className="text-sm text-chakra-crown/90 leading-relaxed">{q.text}</p>
      </div>
      {q.format === "likert" && <LikertScale q={q} value={value} onChange={onChange} testid={`${testid}-likert`} />}
      {q.format === "slider" && <SliderScale q={q} value={value} onChange={onChange} testid={`${testid}-slider`} />}
      {q.format === "mcq"    && <MCQChoice    q={q} value={value} onChange={onChange} testid={`${testid}-mcq`} />}
      {q.safety_note && (
        <p className="text-[10px] text-amber-200/70 italic flex items-start gap-1.5 pt-1">
          <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" /> {q.safety_note}
        </p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Trait Accordion
// ═══════════════════════════════════════════════════════════════════════════

function TraitBlock({ trait, questions, answers, onChange, expanded, onToggle }) {
  const c = TRAIT_COLORS[trait.color] || TRAIT_COLORS.amber;
  const traitQs = questions.filter(q => q.trait === trait.id);
  const answered = traitQs.filter(q => answers[q.id] != null && answers[q.id] !== "").length;
  const total = traitQs.length;
  const pct = total ? Math.round((answered / total) * 100) : 0;
  return (
    <div className={`rounded-2xl border ${c.border} ${c.bgSoft} overflow-hidden`} data-testid={`coo-trait-${trait.id}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`coo-toggle-${trait.id}`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-cosmic-void/30 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full ${c.dot} shadow-[0_0_8px] shadow-current ${c.text} shrink-0`} />
          <div className="text-left min-w-0">
            <h4 className={`font-heading text-base tracking-wide ${c.text}`}>{trait.name.toUpperCase()}</h4>
            <p className="text-[11px] text-chakra-crown/55 mt-0.5 truncate">{trait.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[10px] text-chakra-crown/55 font-heading tracking-wide whitespace-nowrap" data-testid={`coo-progress-${trait.id}`}>
            {answered}/{total}
          </span>
          <div className="w-14 h-1 bg-cosmic-void/60 rounded-full overflow-hidden">
            <div className={`h-full ${c.bar} transition-all`} style={{ width: `${pct}%` }} />
          </div>
          <ChevronDown className={`w-4 h-4 text-chakra-crown/60 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 space-y-3">
              {traitQs.map(q => (
                <QuestionRow key={q.id} q={q} value={answers[q.id]} onChange={(v) => onChange(q.id, v)} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Result panel parts
// ═══════════════════════════════════════════════════════════════════════════

function AxisBar({ label, score }) {
  const pct = score != null ? Math.max(0, Math.min(100, ((score - 1) / 4) * 100)) : 0;
  const isHigh = score != null && score > 3.0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-chakra-crown/70">{label}</span>
        <span className={`font-heading tabular-nums ${isHigh ? "text-rose-300" : "text-emerald-300"}`}>
          {score != null ? score.toFixed(2) : "—"} {score != null && (isHigh ? "· High" : "· Low")}
        </span>
      </div>
      <div className="h-2 bg-cosmic-void/70 rounded-full overflow-hidden">
        <div className={`h-full ${isHigh ? "bg-rose-400" : "bg-emerald-400"} transition-all`}
          style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PerceptionPair({ perception, bucket }) {
  if (!perception) return null;
  return (
    <div className="space-y-2" data-testid="coo-perception">
      <p className="label-sacred text-xs">FALSE & AUTHENTIC PERCEPTION</p>
      <div className="rounded-lg border border-rose-400/30 bg-rose-500/5 p-3">
        <p className="text-[10px] uppercase tracking-wider text-rose-300/80 mb-1">False Belief</p>
        <p className="text-xs text-chakra-crown/90 italic leading-relaxed" data-testid="coo-perception-false">{perception.false}</p>
      </div>
      <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/5 p-3">
        <p className="text-[10px] uppercase tracking-wider text-emerald-300/80 mb-1">Authentic Truth</p>
        <p className="text-xs text-chakra-crown/90 leading-relaxed" data-testid="coo-perception-authentic">{perception.authentic}</p>
      </div>
      <p className="text-[10px] text-chakra-crown/40 italic">Bucket: {BUCKET_LABEL[bucket] || bucket}</p>
    </div>
  );
}

function SafetyBanner({ banner }) {
  if (!banner) return null;
  return (
    <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 space-y-1.5" data-testid={`coo-safety-banner-tier-${banner.tier}`}>
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-300" />
        <p className="text-xs font-heading text-amber-200 tracking-wide">{banner.title}</p>
      </div>
      <p className="text-[11px] text-chakra-crown/85 leading-relaxed">{banner.message}</p>
    </div>
  );
}

function ResultPanel({ result, traits, answers }) {
  if (!result) return null;
  const noScore = result.anxiety_score == null && result.avoidance_score == null;
  const answered = result.answered_count || 0;
  const total = result.total_scored || 19;
  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="flex items-center justify-between text-xs text-chakra-crown/60">
        <span><span className="font-heading text-divine-gold">{answered}</span><span className="text-chakra-crown/40"> / {total} scored questions answered</span></span>
        {result.is_complete && (
          <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 text-[10px] font-heading">COMPLETE</span>
        )}
      </div>

      {noScore && (
        <p className="text-chakra-crown/50 text-sm text-center py-4">
          Answer the questions on the left to see your attachment pattern, origin, and severity here.
        </p>
      )}

      {!noScore && (
        <>
          {/* Classification + Severity + Origin chips */}
          <div className="flex flex-wrap gap-2">
            {result.classification && (
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-heading border ${CLASSIFICATION_COLOR[result.classification]}`} data-testid="coo-classification">
                {CLASSIFICATION_LABEL[result.classification]}
              </span>
            )}
            {result.origin && (
              <span className="px-2.5 py-1 rounded-full text-[11px] font-heading border border-divine-gold/40 bg-divine-gold/10 text-divine-gold" data-testid="coo-origin">
                {ORIGIN_LABEL[result.origin]} Origin
              </span>
            )}
            {result.severity_band && (
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-heading border ${SEVERITY_COLOR[result.severity_band]}`} data-testid="coo-severity">
                Severity · {SEVERITY_LABEL[result.severity_band]}
              </span>
            )}
          </div>

          {/* Axis bars */}
          <div className="space-y-3">
            <AxisBar label="Anxiety axis" score={result.anxiety_score} />
            <AxisBar label="Avoidance axis" score={result.avoidance_score} />
            {result.severity_score != null && (
              <div className="flex items-center justify-between text-[11px] text-chakra-crown/55 pt-1 border-t border-divine-gold/10">
                <span>Severity (mean of 19 items)</span>
                <span className="font-heading text-chakra-crown/85 tabular-nums">{result.severity_score.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Safety banner */}
          <SafetyBanner banner={result.safety_banner} />

          {/* False / Authentic */}
          <PerceptionPair perception={result.perception} bucket={result.bucket} />
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Awareness — 2-column paired component (used by RelationshipDiagnostic)
// ═══════════════════════════════════════════════════════════════════════════

export function CircleOfOriginAwarenessPair({ token }) {
  const [spec, setSpec] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [resultExpanded, setResultExpanded] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [specRes, meRes] = await Promise.all([
        apiCall("GET", "/circle-of-origin/spec", null, token),
        apiCall("GET", "/circle-of-origin/me", null, token),
      ]);
      if (cancelled) return;
      if (specRes.success) setSpec(specRes.data);
      if (meRes.success) {
        setAnswers(meRes.data?.answers || {});
        setResult(meRes.data?.result || null);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleChange = (qid, v) => {
    setAnswers(a => ({ ...a, [qid]: v }));
    setDirty(true);
  };
  const saveAll = async () => {
    setSaving(true);
    const r = await apiCall("PUT", "/circle-of-origin/me", { answers }, token);
    setSaving(false);
    if (r.success) {
      toast.success(`Saved · ${r.data.saved} answer${r.data.saved === 1 ? "" : "s"}`);
      setDirty(false);
      setResult(r.data.result || null);
    } else { toast.error(r.error || "Save failed"); }
  };
  const toggleTrait = (tid) => setExpanded(e => ({ ...e, [tid]: !e[tid] }));
  const expandAll = () => spec && setExpanded(Object.fromEntries(spec.traits.map(t => [t.id, true])));
  const collapseAll = () => setExpanded({});

  if (loading) {
    return (
      <div className="sacred-card flex items-center justify-center py-6 text-chakra-crown/50" data-testid="coo-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading Circle of Origin…
      </div>
    );
  }
  if (!spec) return null;

  const totalScored = (spec.axes?.anxiety?.length || 0) + (spec.axes?.avoidance?.length || 0) + (spec.axes?.load?.length || 0);
  const answeredScored = ["anxiety", "avoidance", "load"].flatMap(ax => spec.axes[ax]).filter(qid => answers[qid] != null && answers[qid] !== "").length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" data-testid="rel-paired-row-coo">
      {/* LEFT — Question / Assessment side */}
      <div className="p-4 rounded-xl border bg-cosmic-light/5 border-divine-gold/10" data-testid="rel-test-card-coo">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-divine-gold/10">
            <Sparkles className="w-5 h-5 text-divine-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-heading text-divine-gold tracking-wide">CIRCLE OF ORIGIN · 4 TRAITS</p>
            <p className="text-xs text-chakra-crown/40 font-heading">Maternal · Paternal · Core Childhood · Adult Attachment</p>
            <p className="text-xs text-chakra-crown/50 mt-1.5 leading-relaxed">{spec.intro?.blurb}</p>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className="text-[10px] text-chakra-crown/50 font-heading">{spec.questions.length} questions</span>
            <div className="flex gap-1.5">
              <button onClick={expandAll} className="text-[10px] text-chakra-crown/55 hover:text-divine-gold px-2 py-0.5 rounded border border-divine-gold/15 hover:border-divine-gold/40 transition-colors" data-testid="coo-expand-all">Expand all</button>
              <button onClick={collapseAll} className="text-[10px] text-chakra-crown/55 hover:text-divine-gold px-2 py-0.5 rounded border border-divine-gold/15 hover:border-divine-gold/40 transition-colors" data-testid="coo-collapse-all">Collapse</button>
            </div>
          </div>
        </div>

        {/* Save / progress bar */}
        <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg bg-cosmic-void/40 border border-divine-gold/10">
          <span className="text-xs text-chakra-crown/60">
            <span className="font-heading text-divine-gold">{answeredScored}</span>
            <span className="text-chakra-crown/40"> / {totalScored} scored questions answered</span>
          </span>
          <button onClick={saveAll} disabled={saving || !dirty}
            data-testid="coo-save-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-divine-gold text-cosmic-void font-heading text-xs hover:bg-divine-gold-light disabled:opacity-40 transition-all">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {dirty ? "Save Assessment" : "Saved"}
          </button>
        </div>

        {/* 4 Traits */}
        <div className="space-y-2.5">
          {spec.traits.map(t => (
            <TraitBlock
              key={t.id}
              trait={t}
              questions={spec.questions}
              answers={answers}
              onChange={handleChange}
              expanded={!!expanded[t.id]}
              onToggle={() => toggleTrait(t.id)}
            />
          ))}
        </div>
        <p className="text-[10px] text-chakra-crown/35 italic pt-3 leading-relaxed">
          Drawn from the <em>Siwon Harmony — Circle of Origin (Root Pattern Assessment)</em>. Anxiety & Avoidance attachment model, mapped to dharmic terms.
        </p>
      </div>

      {/* RIGHT — Result panel */}
      <div className="p-4 rounded-xl border bg-cosmic-light/5 border-divine-gold/10" data-testid="rel-result-card-coo">
        <button
          type="button"
          onClick={() => setResultExpanded(v => !v)}
          className="w-full flex items-start justify-between gap-3 mb-3"
          data-testid="coo-result-toggle"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-divine-gold/10">
              <Sparkles className="w-5 h-5 text-divine-gold" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-heading text-divine-gold tracking-wide">CIRCLE OF ORIGIN · YOUR PATTERN</p>
              <p className="text-xs text-chakra-crown/50 mt-1 leading-relaxed">Classification · Origin · Severity · False / Authentic perception</p>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-chakra-crown/60 transition-transform mt-1 shrink-0 ${resultExpanded ? "rotate-180" : ""}`} />
        </button>
        <AnimatePresence initial={false}>
          {resultExpanded && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <ResultPanel result={result} traits={spec.traits} answers={answers} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Alignment / Awakening / Harmony — adaptive bucket-aware full-width card
// (mirrors PurusharthasCard default-export pattern, used in RelationshipModule.jsx)
// ═══════════════════════════════════════════════════════════════════════════

function PrimaryBucketEmptyState() {
  return (
    <div className="rounded-xl border border-divine-gold/20 bg-cosmic-void/40 p-4 text-xs text-chakra-crown/65 leading-relaxed flex items-start gap-3" data-testid="coo-needs-awareness">
      <AlertCircle className="w-4 h-4 text-divine-gold mt-0.5 shrink-0" />
      <span>
        Complete the <strong>Circle of Origin</strong> assessment on the Awareness tab to surface your personalised practice, wisdom, and harmony content here. The browsable library below remains available regardless.
      </span>
    </div>
  );
}

function CheckableLine({ itemKey, checked, onToggle, color, children, testid }) {
  const c = TRAIT_COLORS[color] || TRAIT_COLORS.amber;
  return (
    <li className={`flex items-start gap-3 text-sm rounded-lg p-3 border ${checked ? "bg-cosmic-void/50 border-divine-gold/25" : "bg-cosmic-void/40 border-divine-gold/10"}`} data-testid={testid}>
      {onToggle ? (
        <button onClick={() => onToggle(itemKey)}
          aria-label={checked ? "Mark incomplete" : "Mark complete"}
          data-testid={`${testid}-check`}
          className={`shrink-0 w-5 h-5 rounded border-[1.5px] flex items-center justify-center transition-all mt-0.5 ${checked ? `${c.bar} border-transparent` : "border-divine-gold/40 hover:border-divine-gold/70"}`}>
          {checked && <Check className="w-3 h-3 text-cosmic-void" strokeWidth={3} />}
        </button>
      ) : (
        <span className={`shrink-0 w-5 h-5 rounded border ${c.border} mt-0.5 flex items-center justify-center`}>
          <Check className={`w-3 h-3 ${c.text} opacity-30`} />
        </span>
      )}
      <div className="flex-1 leading-relaxed text-chakra-crown/85">{children}</div>
    </li>
  );
}

function BucketAlignmentBody({ bucketKey, spec, completedItems, onTogglePipeline, color }) {
  const practice = spec.alignment_practices?.[bucketKey];
  if (!practice) return <p className="text-xs text-chakra-crown/45 italic">No practice defined for this bucket.</p>;
  return (
    <div className="space-y-3" data-testid={`coo-alignment-body-${bucketKey}`}>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-divine-gold/80 mb-1">Practice — {practice.title}</p>
        <p className="text-sm text-chakra-crown/90 leading-relaxed">{practice.practice}</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-chakra-crown/55 mb-2">Journal prompts</p>
        <ul className="space-y-1.5">
          {practice.journal_prompts.map((p, i) => {
            const k = itemKeyFor("alignment", bucketKey, i);
            return (
              <CheckableLine
                key={i} itemKey={k}
                checked={completedItems?.includes(k)}
                onToggle={onTogglePipeline} color={color}
                testid={`coo-alignment-item-${bucketKey}-${i}`}
              >
                {p}
              </CheckableLine>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function BucketAwakeningBody({ bucketKey, spec, completedItems, onTogglePipeline, color }) {
  const wisdom = spec.awakening_wisdom?.[bucketKey];
  if (!wisdom) return <p className="text-xs text-chakra-crown/45 italic">No wisdom defined for this bucket.</p>;
  const k = itemKeyFor("awakening", bucketKey, 0);
  return (
    <ul className="space-y-2" data-testid={`coo-awakening-body-${bucketKey}`}>
      <CheckableLine itemKey={k} checked={completedItems?.includes(k)} onToggle={onTogglePipeline} color={color}
        testid={`coo-awakening-item-${bucketKey}-0`}>
        <span className="italic">{wisdom}</span>
      </CheckableLine>
    </ul>
  );
}

function BucketAccordion({ bucketKey, label, isPrimary, expanded, onToggle, color, progressBadge, children }) {
  const c = TRAIT_COLORS[color] || TRAIT_COLORS.amber;
  return (
    <div className={`rounded-2xl border ${c.border} ${isPrimary ? "ring-1 ring-divine-gold/40 " + c.bgSoft : c.bgSoft} overflow-hidden`} data-testid={`coo-bucket-${bucketKey}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`coo-bucket-toggle-${bucketKey}`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-cosmic-void/30 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`w-2.5 h-2.5 rounded-full ${c.dot} shrink-0`} />
          <div className="text-left min-w-0">
            <h4 className={`font-heading text-sm tracking-wide ${c.text}`}>{label}</h4>
            {isPrimary && <p className="text-[10px] text-divine-gold/80 mt-0.5">Your primary pattern</p>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {progressBadge && (
            <span className="text-[10px] text-chakra-crown/55 font-heading tracking-wide whitespace-nowrap" data-testid={`coo-bucket-progress-${bucketKey}`}>
              {progressBadge}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-chakra-crown/60 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function HarmonyView({ spec, primaryBucket, completedItems, onTogglePipeline }) {
  const universal = spec.harmony_checklist?.universal || [];
  // Tag-based extra items
  const classification = primaryBucket?.startsWith("anxious") ? "anxious"
    : primaryBucket?.startsWith("avoidant") ? "avoidant"
    : primaryBucket === "disorganized_any" ? "disorganized"
    : primaryBucket === "secure_any" ? "secure" : null;
  const tagged = classification ? (spec.harmony_checklist?.[classification] || []) : [];

  const [openUniversal, setOpenUniversal] = useState(true);
  const [openPattern, setOpenPattern] = useState(true);

  const universalDone = universal.filter((_, i) => completedItems?.includes(itemKeyFor("harmony", "universal", i))).length;
  const taggedDone = classification
    ? tagged.filter((_, i) => completedItems?.includes(itemKeyFor("harmony", classification, i))).length
    : 0;

  return (
    <div className="space-y-2.5" data-testid="coo-harmony-body">
      {/* Universal — collapsible */}
      <BucketAccordion
        bucketKey="universal"
        label="Universal — for everyone"
        isPrimary={false}
        color="amber"
        expanded={openUniversal}
        onToggle={() => setOpenUniversal(v => !v)}
        progressBadge={`${universalDone}/${universal.length}`}
      >
        <ul className="space-y-1.5">
          {universal.map((item, i) => {
            const k = itemKeyFor("harmony", "universal", i);
            return (
              <CheckableLine
                key={i} itemKey={k}
                checked={completedItems?.includes(k)}
                onToggle={onTogglePipeline} color="amber"
                testid={`coo-harmony-item-universal-${i}`}
              >{item}</CheckableLine>
            );
          })}
        </ul>
      </BucketAccordion>

      {/* Pattern-tagged — collapsible */}
      {classification && tagged.length > 0 && (
        <BucketAccordion
          bucketKey={`pattern-${classification}`}
          label={`For your pattern · ${classification.charAt(0).toUpperCase() + classification.slice(1)}`}
          isPrimary={true}
          color={classification === "anxious" ? "rose" : classification === "avoidant" ? "sky" : classification === "disorganized" ? "violet" : "amber"}
          expanded={openPattern}
          onToggle={() => setOpenPattern(v => !v)}
          progressBadge={`${taggedDone}/${tagged.length}`}
        >
          <ul className="space-y-1.5">
            {tagged.map((item, i) => {
              const k = itemKeyFor("harmony", classification, i);
              return (
                <CheckableLine
                  key={i} itemKey={k}
                  checked={completedItems?.includes(k)}
                  onToggle={onTogglePipeline} color={classification === "anxious" ? "rose" : classification === "avoidant" ? "sky" : classification === "disorganized" ? "violet" : "amber"}
                  testid={`coo-harmony-item-${classification}-${i}`}
                >{item}</CheckableLine>
              );
            })}
          </ul>
        </BucketAccordion>
      )}
      {!classification && (
        <p className="text-[10px] text-chakra-crown/45 italic px-2">Complete the Circle of Origin Awareness assessment to unlock pattern-specific harmony items.</p>
      )}
    </div>
  );
}

const TAB_HEADER = {
  alignment: { title: "CIRCLE OF ORIGIN · ALIGNMENT", sub: "Your personalised attachment-pattern practice + journal prompts. Tick items to feed your weekly chart." },
  awakening: { title: "CIRCLE OF ORIGIN · AWAKENING", sub: "Dharmic wisdom for your specific origin pattern. Contemplate, then tick when held." },
  harmony:   { title: "CIRCLE OF ORIGIN · HARMONY",   sub: "Achieved-state checklist — universal items plus your pattern-specific items." },
};

export default function CircleOfOriginCard({ tab = "alignment", token, completedItems, onTogglePipeline }) {
  const [spec, setSpec] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [specRes, meRes] = await Promise.all([
        apiCall("GET", "/circle-of-origin/spec", null, token),
        apiCall("GET", "/circle-of-origin/me", null, token),
      ]);
      if (cancelled) return;
      if (specRes.success) setSpec(specRes.data);
      if (meRes.success) setMe(meRes.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  const primaryBucket = me?.result?.bucket || null;

  // Auto-expand the primary bucket once spec + me loaded
  useEffect(() => {
    if (spec && primaryBucket && Object.keys(expanded).length === 0) {
      setExpanded({ [primaryBucket]: true });
    }
  }, [spec, primaryBucket, expanded]);

  if (loading) {
    return (
      <div className="sacred-card flex items-center justify-center py-6 text-chakra-crown/50" data-testid="coo-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading Circle of Origin…
      </div>
    );
  }
  if (!spec) return null;
  const header = TAB_HEADER[tab] || TAB_HEADER.alignment;
  const otherBuckets = ALL_BUCKETS.filter(b => b !== primaryBucket);
  const toggleBucket = (k) => setExpanded(e => ({ ...e, [k]: !e[k] }));

  const renderBucketBody = (bucketKey, color) => {
    if (tab === "alignment") return <BucketAlignmentBody bucketKey={bucketKey} spec={spec} completedItems={completedItems} onTogglePipeline={onTogglePipeline} color={color} />;
    if (tab === "awakening") return <BucketAwakeningBody bucketKey={bucketKey} spec={spec} completedItems={completedItems} onTogglePipeline={onTogglePipeline} color={color} />;
    return null;
  };

  return (
    <div className="sacred-card" data-testid={`coo-card-${tab}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-divine-gold/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-divine-gold" />
          </div>
          <div>
            <h3 className="font-heading text-base text-divine-gold tracking-wide">{header.title}</h3>
            <p className="text-xs text-chakra-crown/55 mt-1 leading-relaxed">{header.sub}</p>
          </div>
        </div>
      </div>

      {/* HARMONY uses the dedicated combined view */}
      {tab === "harmony" ? (
        <>
          {!primaryBucket && <PrimaryBucketEmptyState />}
          <div className="mt-3">
            <HarmonyView spec={spec} primaryBucket={primaryBucket} completedItems={completedItems} onTogglePipeline={onTogglePipeline} />
          </div>
        </>
      ) : (
        <>
          {/* Empty-state if Awareness not complete */}
          {!primaryBucket && <PrimaryBucketEmptyState />}

          <div className="space-y-2.5">
            {/* Primary bucket — auto-expanded */}
            {primaryBucket && (
              <BucketAccordion
                bucketKey={primaryBucket}
                label={BUCKET_LABEL[primaryBucket] || primaryBucket}
                isPrimary
                color="amber"
                expanded={!!expanded[primaryBucket]}
                onToggle={() => toggleBucket(primaryBucket)}
              >
                {renderBucketBody(primaryBucket, "amber")}
              </BucketAccordion>
            )}

            {/* Other 7 buckets — collapsed */}
            <p className="text-[10px] uppercase tracking-wider text-chakra-crown/45 pt-3 pb-1">Browse the other patterns</p>
            {otherBuckets.map(b => (
              <BucketAccordion
                key={b}
                bucketKey={b}
                label={BUCKET_LABEL[b]}
                isPrimary={false}
                color={b.startsWith("anxious") ? "rose" : b.startsWith("avoidant") ? "sky" : b === "disorganized_any" ? "violet" : "amber"}
                expanded={!!expanded[b]}
                onToggle={() => toggleBucket(b)}
              >
                {renderBucketBody(b, "amber")}
              </BucketAccordion>
            ))}
          </div>
        </>
      )}

      <p className="text-[10px] text-chakra-crown/35 italic pt-4 leading-relaxed">
        Drawn from <em>Siwon Harmony — Circle of Origin (Root Pattern Assessment)</em>. Each tab adapts to your specific attachment classification once Awareness is complete.
      </p>
    </div>
  );
}
